/**
 * React Query hooks for the True Odds service.
 * 
 * These hooks provide caching, background refetching, and optimistic updates.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from './index';

// =============================================================================
// Query Keys (for cache invalidation)
// =============================================================================

export const queryKeys = {
  health: ['health'],
  status: ['status'],
  systemStatus: ['systemStatus'],
  // quotes takes filter params as JSON string for cache key uniqueness
  quotes: (params) => ['quotes', JSON.stringify(params || {})],
  quotesByFixture: (fixtureId) => ['quotesByFixture', fixtureId || ''],
  quotesSummary: (excludeLive) => ['quotesSummary', excludeLive],
  quoteMovers: (limit, hours) => ['quoteMovers', limit, hours],
  quote: (key) => ['quote', key],
  quoteHistory: (key, hours) => ['quoteHistory', key, hours],
  quotePrices: (key, hours, line, panelTs = null) => ['quotePrices', key, hours, line, panelTs],
  quoteDiagnostics: (key, hours, type) => ['quoteDiagnostics', key, hours, type],
  competitorSignals: (key, hours, book, event) => ['competitorSignals', key, hours, book || '', event || ''],
  entriesDiagnostics: (key, hours, series) => ['entriesDiagnostics', key, hours, series],
  quoteLines: (key, hours) => ['quoteLines', key, hours],
  quoteLadder: (key, hours) => ['quoteLadder', key, hours],
  entryStatsByLine: (key) => ['entryStatsByLine', key],
  quoteUpdates: (key, hours) => ['quoteUpdates', key, hours],
  /** Third segment: quote line string so Taken rollups refetch when baseline / displayed line changes */
  orderBook: (key, lineKey = '') => ['orderBook', key, lineKey],
  orderBookClearLog: (key, limit) => ['orderBookClearLog', key, limit],
  masterActivityLog: (key, hours, limit) => ['masterActivityLog', key, hours, limit],
  marketEntries: (key, params) => ['marketEntries', key, JSON.stringify(params || {})],
  recentlyEnteredMarkets: (params) => ['recentlyEnteredMarkets', JSON.stringify(params || {})],
  recentEntries: (params) => ['recentEntries', JSON.stringify(params || {})],
  // Prefix for invalidate/patch across every cached range; the fn is the exact key.
  gradedSlips: (params) => ['gradedSlips', JSON.stringify(params || {})],
  entriesExposure: (params) => ['entriesExposure', JSON.stringify(params || {})],
  hotCompositions: (params) => ['hotCompositions', JSON.stringify(params || {})],
  quotedMarketsActivity: (params) => ['quotedMarketsActivity', JSON.stringify(params || {})],
  doneFixtures: ['doneFixtures'],
  scheduledFixtures: ['scheduledFixtures'],
  activeFixturesSummary: ['activeFixturesSummary'],
  markets: (fixture) => ['markets', fixture || 'all'],
  market: (key) => ['market', key],
  filters: ['filters'],
  filterLeagues: ['filterLeagues'],
  filterMarketIds: ['filterMarketIds'],
  filterTeams: ['filterTeams'],
  filterPlayers: ['filterPlayers'],
  gatedMarkets: ['gatedMarkets'],
  derivedMarketsActive: (params = {}) => ['derivedMarketsActive', JSON.stringify(params || {})],
  derivedModelDetail: (marketKey) => ['derivedModelDetail', marketKey],
  manualInterventions: ['manualInterventions'],
  obOrderBookClearEvents: (limit, hours, source, qualifyingOnly) => [
    'obOrderBookClearEvents',
    limit,
    hours ?? null,
    source ?? null,
    Boolean(qualifyingOnly),
  ],
  marketOverride: (key) => ['marketOverride', key],
  batchPublishSuspensions: (activeOnly, sportBucket) => [
    'batchPublishSuspensions',
    Boolean(activeOnly),
    sportBucket || 'all',
  ],
  batchCompetitorMutes: (activeOnly, sportBucket) => [
    'batchCompetitorMutes',
    Boolean(activeOnly),
    sportBucket || 'all',
  ],
  config: ['config'],
  configOverridesSummary: ['configOverridesSummary'],
  configOverridesView: (profile, marketId) => ['configOverridesView', profile, marketId || ''],
  configLimits: (params) => ['configLimits', JSON.stringify(params || {})],
  limitFilterOptions: ['limitFilterOptions'],
  limitModifiers: ['limitModifiers'],
  multiplierModifiers: ['multiplierModifiers'],
  optaLiveFixtures: (retentionDays, liveCoveredOnly, lookaheadDays) => [
    'optaLiveFixtures',
    retentionDays,
    liveCoveredOnly,
    lookaheadDays,
  ],
  optaLiveDashboard: (fixtureId, optaId) => ['optaLiveDashboard', fixtureId, optaId || ''],
  liveKillSwitch: ['liveKillSwitch'],
  modelMarketRules: (scope = {}) => [
    'modelMarketRules',
    scope.sport_bucket || scope.sportBucket || 'wnba',
    scope.sport || 'basketball',
    scope.league || 'wnba',
  ],
  modelMarketRegistry: (scope = {}) => [
    'modelMarketRegistry',
    scope.sport_bucket || scope.sportBucket || 'wnba',
    scope.sport || 'basketball',
    scope.league || 'wnba',
  ],
  modelMarketObservations: (modelName, fixtureId, marketId, scope = {}) => [
    'modelMarketObservations',
    modelName || '',
    fixtureId || '',
    marketId || '',
    scope.sport_bucket || scope.sportBucket || 'wnba',
  ],
};

/** Bypass staleTime so GET /quote reflects writer state immediately after mutations. */
async function prefetchFreshQuote(queryClient, marketKey) {
  await queryClient.fetchQuery({
    queryKey: queryKeys.quote(marketKey),
    queryFn: () => api.fetchQuote(marketKey),
  });
}

// =============================================================================
// Health & Status Hooks
// =============================================================================

export const useHealth = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: api.fetchHealth,
    staleTime: 10_000, // 10s
    refetchInterval: 30_000, // 30s
    ...options,
  });
};

export const useStatus = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.status,
    queryFn: api.fetchStatus,
    staleTime: 5_000,
    refetchInterval: 10_000,
    ...options,
  });
};

export const useSystemStatus = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.systemStatus,
    queryFn: api.fetchSystemStatus,
    staleTime: 5_000,
    refetchInterval: 10_000,
    ...options,
  });
};

// =============================================================================
// Quote Hooks
// =============================================================================

export const useQuote = (marketKey, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quote(marketKey),
    queryFn: () => api.fetchQuote(marketKey),
    enabled: !!marketKey,
    // Mutations call prefetchFreshQuote; keep quote eager so poll + manual refetch stay responsive.
    staleTime: 0,
    refetchInterval: 5_000, // 5s
    ...options,
  });
};

/**
 * Fetch quotes with server-side filtering and pagination.
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Max results (default 100)
 * @param {number} params.offset - Skip first N results
 * @param {string} params.sort_by - Sort order: last_sse_update, timestamp, p_fair, n_books
 * @param {boolean} params.exclude_live - Exclude live fixtures (default true)
 * @param {string} params.league - Filter by league (partial match)
 * @param {string} params.market_id - Filter by market_id (exact match)
 * @param {string} params.player_name - Filter by player name (partial match)
 * @param {string} params.team_name - Filter by team name (partial match)
 * @param {boolean} params.gate_allowed - Filter by gate status
 * @param {number} params.min_books - Filter by minimum books
 * @param {Object} options - React Query options
 */
export const useAllQuotes = (params = {}, options = {}) => {
  // Default to 100 limit and exclude live
  const queryParams = {
    limit: 100,
    exclude_live: true,
    ...params,
  };
  
  return useQuery({
    queryKey: queryKeys.quotes(queryParams),
    queryFn: () => api.fetchAllQuotes(queryParams),
    staleTime: 2_000,
    refetchInterval: 5_000,
    ...options,
  });
};

/**
 * All quotes for a single fixture (used e.g. to list a player’s other markets in the same game).
 * @param {string} fixtureId - Optic / internal fixture id (first segment of market_key)
 */
export const useQuotesByFixture = (fixtureId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quotesByFixture(fixtureId),
    queryFn: () => api.fetchQuotesByFixture(fixtureId),
    enabled: Boolean(fixtureId),
    staleTime: 5_000,
    refetchInterval: 10_000,
    ...options,
  });
};

/**
 * Fetch summary stats and available filter options without loading full quote data.
 * Counts (total, gated, blocked) respect filter params when provided.
 * @param {Object} filterParams - Same shape as all-quotes filters (exclude_live, league, market_id, player_name, team_name, gate_allowed, min_books)
 */
export const useQuotesSummary = (filterParams = {}, options = {}) => {
  const params = {
    exclude_live: filterParams.exclude_live !== false,
    ...(filterParams.league && { league: filterParams.league }),
    ...(filterParams.market_id && { market_id: filterParams.market_id }),
    ...(filterParams.player_name && { player_name: filterParams.player_name }),
    ...(filterParams.team_name && { team_name: filterParams.team_name }),
    ...(filterParams.gate_allowed !== undefined && filterParams.gate_allowed !== '' && { gate_allowed: filterParams.gate_allowed === 'true' }),
    ...(filterParams.min_books && { min_books: filterParams.min_books }),
  };
  return useQuery({
    queryKey: ['quotesSummary', params],
    queryFn: () => api.fetchQuotesSummary(params),
    staleTime: 30_000,
    refetchInterval: 60_000,
    ...options,
  });
};

/**
 * Fetch markets with highest innovation (price movement) in the last N hours.
 */
export const useQuoteMovers = (limit = 100, hours = 1.0, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quoteMovers(limit, hours),
    queryFn: () => api.fetchQuoteMovers({ limit, hours }),
    staleTime: 10_000, // 10s
    refetchInterval: 30_000, // 30s
    ...options,
  });
};

export const useQuoteHistory = (marketKey, hours = 4, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quoteHistory(marketKey, hours),
    queryFn: () => api.fetchQuoteHistory(marketKey, { hours }),
    enabled: !!marketKey,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

/** ``line`` null = follow the quote (server targets each sample's own KF baseline). */
export const useQuotePriceHistory = (
  marketKey,
  hours = 4,
  line = null,
  options = {},
  panelTs = null,
) => {
  return useQuery({
    queryKey: queryKeys.quotePrices(marketKey, hours, line, panelTs),
    queryFn: () => api.fetchQuotePriceHistory(marketKey, { hours, line, panelTs }),
    // Keep the previous payload while a scrub refetch is in flight so the raw traces do
    // not blank out between panel timestamps.
    placeholderData: (prev) => prev,
    enabled: !!marketKey,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useQuoteDiagnosticsHistory = (marketKey, hours = 4, diagnosticType = 'gating', options = {}) => {
  return useQuery({
    queryKey: queryKeys.quoteDiagnostics(marketKey, hours, diagnosticType),
    queryFn: () => api.fetchQuoteDiagnosticsHistory(marketKey, { hours, diagnostic_type: diagnosticType }),
    enabled: !!marketKey,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useCompetitorSignals = (marketKey, hours = 6, filters = {}, options = {}) => {
  const { sportsbook, event } = filters;
  return useQuery({
    queryKey: queryKeys.competitorSignals(marketKey, hours, sportsbook, event),
    queryFn: () => api.fetchCompetitorSignals(marketKey, { hours, sportsbook, event }),
    enabled: !!marketKey,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useEntriesDiagnosticsHistory = (marketKey, hours = 6, series = 'P_post', options = {}) => {
  return useQuery({
    queryKey: queryKeys.entriesDiagnostics(marketKey, hours, series),
    queryFn: () => api.fetchEntriesDiagnosticsHistory(marketKey, { hours, series }),
    enabled: !!marketKey,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useQuoteLines = (marketKey, hours = 4, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quoteLines(marketKey, hours),
    queryFn: () => api.fetchQuoteLines(marketKey, { hours }),
    enabled: !!marketKey,
    staleTime: 30_000,
    ...options,
  });
};

export const useQuoteLadder = (marketKey, hours = 4, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quoteLadder(marketKey, hours),
    queryFn: () => api.fetchQuoteLadder(marketKey, { hours }),
    enabled: !!marketKey,
    // Keep in sync with useQuote so ladder rows / margination track live writer state,
    // not only manual gate mutations (those also explicitly refetch ladder).
    staleTime: 2_000,
    refetchInterval: 5_000,
    ...options,
  });
};

export const useEntryStatsByLine = (marketKey, options = {}) => {
  return useQuery({
    queryKey: queryKeys.entryStatsByLine(marketKey),
    queryFn: () => api.fetchEntryStatsByLine(marketKey),
    enabled: !!marketKey,
    staleTime: 2_000,
    refetchInterval: 5_000,
    ...options,
  });
};

export const useQuoteUpdates = (marketKey, hours = 4, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quoteUpdates(marketKey, hours),
    queryFn: () => api.fetchQuoteUpdates(marketKey, { hours }),
    enabled: !!marketKey,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

/** Live internal OB ladder (P_to_OB + remaining depth). Pass ``quoteLine`` when tied to dashboard quote line. */
export const useOrderBook = (marketKey, options = {}) => {
  const { quoteLine, ...queryOpts } = options;
  const lineKey =
    quoteLine != null && Number.isFinite(Number(quoteLine)) ? String(Number(quoteLine)) : '';
  return useQuery({
    queryKey: queryKeys.orderBook(marketKey, lineKey),
    queryFn: () => api.fetchOrderBook(marketKey),
    enabled: !!marketKey,
    staleTime: 2_000,
    refetchInterval: 5_000,
    ...queryOpts,
  });
};

/** Manual clear at one ladder rung (dashboard operator action; no sweep from top). */
export const useOrderBookClearMutation = (marketKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ side, index, pull_from_opposite_top = false, acted_by: actedBy }) =>
      api.postOrderBookClearLevel(marketKey, {
        side,
        index,
        pull_from_opposite_top: Boolean(pull_from_opposite_top),
        ...(actedBy != null && String(actedBy).trim() ? { acted_by: String(actedBy).trim() } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['orderBookClearLog', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['masterActivityLog', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['competitorSignals', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['entriesDiagnostics', marketKey] });
    },
  });
};

/** Refill one rung from opposite-side depth (internal OB bookkeeping only). */
export const useOrderBookAddLevelMutation = (marketKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ side, index, acted_by: actedBy }) =>
      api.postOrderBookAddLevel(marketKey, {
        side,
        index,
        ...(actedBy != null && String(actedBy).trim() ? { acted_by: String(actedBy).trim() } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['masterActivityLog', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['orderBookClearLog', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['competitorSignals', marketKey] });
    },
  });
};

/** Append one deeper tail rung on one side only (no opposite-side stake). */
export const useOrderBookBuilderApplyMutation = (marketKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.postOrderBookBuilderApply(marketKey, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quote', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['orderBookClearLog', marketKey] });
    },
  });
};

export const useOrderBookBuilderClearMutation = (marketKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (actedBy) => api.deleteOrderBookBuilder(marketKey, actedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quote', marketKey] });
    },
  });
};

export const useOrderBookAppendBottomMutation = (marketKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ side, acted_by: actedBy }) =>
      api.postOrderBookAppendBottom(marketKey, {
        side,
        ...(actedBy != null && String(actedBy).trim() ? { acted_by: String(actedBy).trim() } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['masterActivityLog', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['orderBookClearLog', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
    },
  });
};

/** Full OB depth re-seed on writer (proxied from api_only). */
export const useOrderBookRefreshDepthMutation = (marketKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mode = 'two_sided',
      acted_by: actedBy,
      override_soft_gates: overrideSoft,
      persist_force_two_sided: persistForceTwoSided,
      persist_force_one_sided: persistForceOneSided,
    } = {}) => {
      const body = {
        mode,
        ...(actedBy != null && String(actedBy).trim() ? { acted_by: String(actedBy).trim() } : {}),
        override_soft_gates: Boolean(overrideSoft),
        persist_force_two_sided: Boolean(persistForceTwoSided),
        persist_force_one_sided: Boolean(persistForceOneSided),
      };
      return api.postOrderBookRefreshDepth(marketKey, body);
    },
    onError: (err) => {
      const detail = err?.response?.data ?? err?.message ?? err;
      // eslint-disable-next-line no-console -- visible failure for operators
      console.warn('[order-book] depth refresh failed', marketKey, detail);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['masterActivityLog', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['orderBookClearLog', marketKey] });
      if (data?.applied_manual_shaping) {
        queryClient.invalidateQueries({ queryKey: queryKeys.marketOverride(marketKey) });
        queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      }
    },
  });
};

/** OB liquidity clear audit (manual + entry path). */
export const useOrderBookClearLog = (marketKey, limit = 100, options = {}) => {
  return useQuery({
    queryKey: queryKeys.orderBookClearLog(marketKey, limit),
    queryFn: () => api.fetchOrderBookClearLog(marketKey, limit),
    enabled: !!marketKey,
    staleTime: 5_000,
    refetchInterval: 30_000,
    ...options,
  });
};

/** Activity log: base KF + EntriesKF diagnostics + OB clears (merged timeline). */
export const useMasterActivityLog = (marketKey, params = {}, options = {}) => {
  const { hours = 168, limit = 400 } = params;
  return useQuery({
    queryKey: queryKeys.masterActivityLog(marketKey, hours, limit),
    queryFn: () => api.fetchMasterActivityLog(marketKey, { hours, limit }),
    enabled: !!marketKey,
    staleTime: 5_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useMarketEntries = (marketKey, params = {}, options = {}) => {
  const { limit = 50, min_sharp, min_fee } = params;
  return useQuery({
    queryKey: queryKeys.marketEntries(marketKey, params),
    queryFn: () => api.fetchMarketEntries(marketKey, { limit, min_sharp, min_fee }),
    enabled: !!marketKey,
    staleTime: 15_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useRecentlyEnteredMarkets = (params = {}, options = {}) => {
  const { minutes, limit = 50 } = params;
  return useQuery({
    queryKey: queryKeys.recentlyEnteredMarkets(params),
    queryFn: () => api.fetchRecentlyEnteredMarkets({ minutes, limit }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    ...options,
  });
};

/**
 * Entries view: past N minutes of entries with sort/filter.
 */
export const useRecentEntries = (params = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.recentEntries(params),
    queryFn: () => api.fetchRecentEntries(params),
    staleTime: 15_000,
    refetchInterval: 30_000,
    ...options,
  });
};

/** Graded Pub/Sub event log / latest slip state. */
export const useGradedSlips = (params = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.gradedSlips(params),
    queryFn: () => api.fetchGradedSlips(params),
    staleTime: 10_000,
    refetchInterval: 15_000,
    ...options,
  });
};

/**
 * Entries page — exposure aggregate (fees, liability, sharp per market × player × fixture × line).
 */
export const useEntriesExposure = (params = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.entriesExposure(params),
    queryFn: () => api.fetchEntriesExposure(params),
    staleTime: 15_000,
    refetchInterval: 30_000,
    ...options,
  });
};

/** Hot replicated parlay compositions (Entries → Replication tab). */
export const useHotCompositions = (params = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.hotCompositions(params),
    queryFn: () => api.fetchHotCompositions(params),
    staleTime: 15_000,
    refetchInterval: 30_000,
    ...options,
  });
};

/** Quoted markets with in-window entry fee totals (Markets tab). */
export const useQuotedMarketsActivity = (params = {}, options = {}) => {
  return useQuery({
    queryKey: queryKeys.quotedMarketsActivity(params),
    queryFn: () => api.fetchQuotedMarketsActivity(params),
    staleTime: 5_000,
    refetchInterval: 15_000,
    ...options,
  });
};

/** Derived-rule targets with snapshot + DB quote + liability rollups (Markets → Sleeper models). */
export const useDerivedMarketsActive = (params = {}, options = {}) => {
  const { enabled = true, ...rest } = options;
  const q = {
    minutes: 30,
    sort_by: 'liability',
    sort_order: 'desc',
    limit: 100,
    ...params,
  };
  return useQuery({
    queryKey: queryKeys.derivedMarketsActive(q),
    queryFn: () => api.fetchDerivedMarketsActive(q),
    staleTime: 5_000,
    refetchInterval: 15_000,
    ...rest,
    enabled,
  });
};

/** Single derived target breakdown for dashboard Models tab (empty payload when not a derived target). */
export const useDerivedModelDetail = (marketKey, options = {}) => {
  const { enabled = true, ...rest } = options;
  return useQuery({
    queryKey: queryKeys.derivedModelDetail(marketKey),
    queryFn: () => api.fetchDerivedModelDetail(marketKey),
    enabled: !!marketKey && enabled,
    staleTime: 5_000,
    refetchInterval: 15_000,
    retry: (failureCount, err) => {
      if (err?.response?.status === 404) return false;
      return failureCount < 2;
    },
    ...rest,
  });
};

// =============================================================================
// Fixture Hooks
// =============================================================================

export const useDoneFixtures = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.doneFixtures,
    queryFn: api.fetchDoneFixtures,
    staleTime: 60_000, // 1 min
    refetchInterval: 5 * 60_000, // 5 min
    ...options,
  });
};

export const useScheduledFixtures = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.scheduledFixtures,
    queryFn: api.fetchScheduledFixtures,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    ...options,
  });
};

export const useActiveFixturesSummary = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.activeFixturesSummary,
    queryFn: api.fetchActiveFixturesSummary,
    staleTime: 15_000,
    refetchInterval: 30_000,
    ...options,
  });
};

// =============================================================================
// Market Hooks
// =============================================================================

export const useMarkets = (fixtureId = null, options = {}) => {
  return useQuery({
    queryKey: queryKeys.markets(fixtureId),
    queryFn: () => api.fetchMarkets(fixtureId),
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useMarket = (marketKey, options = {}) => {
  return useQuery({
    queryKey: queryKeys.market(marketKey),
    queryFn: () => api.fetchMarket(marketKey),
    enabled: !!marketKey,
    staleTime: 5_000,
    refetchInterval: 15_000,
    ...options,
  });
};

// =============================================================================
// Filter Hooks
// =============================================================================

export const useFilters = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.filters,
    queryFn: api.fetchFilters,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    ...options,
  });
};

export const useFilterLeagues = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.filterLeagues,
    queryFn: api.fetchFilterLeagues,
    staleTime: 60_000,
    ...options,
  });
};

export const useFilterMarketIds = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.filterMarketIds,
    queryFn: api.fetchFilterMarketIds,
    staleTime: 60_000,
    ...options,
  });
};

export const useFilterTeams = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.filterTeams,
    queryFn: api.fetchFilterTeams,
    staleTime: 60_000,
    ...options,
  });
};

export const useFilterPlayers = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.filterPlayers,
    queryFn: api.fetchFilterPlayers,
    staleTime: 60_000,
    ...options,
  });
};

// =============================================================================
// Manual Gating Hooks
// =============================================================================

export const useGatedMarkets = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.gatedMarkets,
    queryFn: api.fetchGatedMarkets,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useManualInterventions = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.manualInterventions,
    queryFn: api.fetchManualInterventions,
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

export const useObOrderBookClearEvents = (params = {}, options = {}) => {
  const { limit = 100, hours = 6, source = null, qualifyingOnly = true } = params;
  return useQuery({
    queryKey: queryKeys.obOrderBookClearEvents(limit, hours, source, qualifyingOnly),
    queryFn: () =>
      api.fetchObOrderBookClearEvents({ limit, hours, source, qualifyingOnly }),
    staleTime: 10_000,
    refetchInterval: 30_000,
    ...options,
  });
};

/** Active batch Pub/Sub suspensions (requires api_only backend). */
export const useBatchPublishSuspensions = (
  activeOnly = true,
  sportBucket = null,
  options = {}
) => {
  return useQuery({
    queryKey: queryKeys.batchPublishSuspensions(activeOnly, sportBucket),
    queryFn: () =>
      api.fetchBatchPublishSuspensions({
        activeOnly,
        limit: 200,
        sportBucket: sportBucket || undefined,
      }),
    staleTime: 5_000,
    ...options,
  });
};

export const useCreateBatchPublishSuspension = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createBatchPublishSuspension(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchPublishSuspensions'] });
    },
  });
};

export const useRemoveBatchPublishSuspension = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.removeBatchPublishSuspension(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchPublishSuspensions'] });
    },
  });
};

/** Active batch competitor mute rules (requires api_only backend). */
export const useBatchCompetitorMutes = (
  activeOnly = true,
  sportBucket = null,
  options = {}
) => {
  return useQuery({
    queryKey: queryKeys.batchCompetitorMutes(activeOnly, sportBucket),
    queryFn: () =>
      api.fetchBatchCompetitorMutes({
        activeOnly,
        limit: 200,
        sportBucket: sportBucket || undefined,
      }),
    staleTime: 5_000,
    ...options,
  });
};

export const useCreateBatchCompetitorMute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createBatchCompetitorMute(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchCompetitorMutes'] });
    },
  });
};

export const useRemoveBatchCompetitorMute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.removeBatchCompetitorMute(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchCompetitorMutes'] });
    },
  });
};

export const useMarketOverride = (marketKey, options = {}) => {
  return useQuery({
    queryKey: queryKeys.marketOverride(marketKey),
    queryFn: () => api.fetchMarketOverride(marketKey),
    enabled: !!marketKey,
    staleTime: 5_000,
    ...options,
  });
};

/**
 * Mutation to gate a market.
 * Automatically invalidates relevant queries on success.
 */
export const useGateMarket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    // Only forward keys the caller set. A fixed opts object used to always include
    // manual_logit_margin_scale (undefined → JSON null), which tripped the writer's
    // independent-only gate path and skipped real suspend/shaping.
    mutationFn: ({ marketKey, gatedBy = 'manual', ...payload }) =>
      api.gateMarket(marketKey, { gatedBy, ...payload }),
    onSuccess: async (data, { marketKey }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gatedMarkets });
      queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      queryClient.invalidateQueries({ queryKey: ['quotedMarketsActivity'] });
      queryClient.invalidateQueries({ queryKey: ['optaLiveDashboard'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketOverride(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteLadder', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.entryStatsByLine(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['quoteUpdates', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      await prefetchFreshQuote(queryClient, marketKey);
      await queryClient.refetchQueries({ queryKey: ['quoteLadder', marketKey] });
    },
  });
};

/**
 * Mutation to ungate a market.
 * Automatically invalidates relevant queries on success.
 */
export const useUngateMarket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (marketKey) => api.ungateMarket(marketKey),
    onSuccess: async (data) => {
      const marketKey = data.market_key;
      queryClient.invalidateQueries({ queryKey: queryKeys.gatedMarkets });
      queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      queryClient.invalidateQueries({ queryKey: ['quotedMarketsActivity'] });
      queryClient.invalidateQueries({ queryKey: ['optaLiveDashboard'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketOverride(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['quotesByFixture'] });
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteLadder', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.entryStatsByLine(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['quoteUpdates', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      await prefetchFreshQuote(queryClient, marketKey);
      await queryClient.refetchQueries({ queryKey: ['quoteLadder', marketKey] });
    },
  });
};

export const useBulkGateMarkets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.bulkGateMarkets(body),
    onSuccess: async () => {
      // Light invalidation only — callers (e.g. Overview confirm) refetch fixture quotes once.
      // Avoid per-market ladder/prefetch thrash that useGateMarket does.
      queryClient.invalidateQueries({ queryKey: queryKeys.gatedMarkets });
      queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['quotesByFixture'] });
      queryClient.invalidateQueries({ queryKey: ['quotedMarketsActivity'] });
    },
  });
};

export const useSetManualBaselineLock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ marketKey, baseLine, setBy }) =>
      api.setManualBaselineLock(marketKey, { baseLine, setBy }),
    onSuccess: async (data) => {
      const marketKey = data.market_key;
      queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketOverride(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteLadder', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.entryStatsByLine(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['quoteUpdates', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      await prefetchFreshQuote(queryClient, marketKey);
      await queryClient.refetchQueries({ queryKey: ['quoteLadder', marketKey] });
    },
  });
};

export const useClearManualBaselineLock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (marketKey) => api.clearManualBaselineLock(marketKey),
    onSuccess: async (data) => {
      const marketKey = data.market_key;
      queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketOverride(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteLadder', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.entryStatsByLine(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['quoteUpdates', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      await prefetchFreshQuote(queryClient, marketKey);
      await queryClient.refetchQueries({ queryKey: ['quoteLadder', marketKey] });
    },
  });
};

export const useSetMuteCompetitorSignals = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ marketKey, enabled }) => api.setMuteCompetitorSignals(marketKey, { enabled }),
    onSuccess: async (data) => {
      const marketKey = data.market_key;
      queryClient.invalidateQueries({ queryKey: queryKeys.gatedMarkets });
      queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketOverride(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteLadder', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.entryStatsByLine(marketKey) });
      queryClient.invalidateQueries({ queryKey: ['quoteUpdates', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['competitorSignals', marketKey] });
      await prefetchFreshQuote(queryClient, marketKey);
      await queryClient.refetchQueries({ queryKey: ['quoteLadder', marketKey] });
    },
  });
};

/** Inject EntriesKF variance (P += ΔP). Invalidates quote / OB / variance charts. */
export const useInjectMarketUncertainty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ marketKey, deltaP, actedBy }) =>
      api.injectMarketUncertainty(marketKey, { deltaP, actedBy }),
    onSuccess: async (data) => {
      const marketKey = data.market_key;
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteLadder', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteUpdates', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['diagnostics', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['entriesDiagnostics', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['competitorSignals', marketKey] });
      await prefetchFreshQuote(queryClient, marketKey);
      await queryClient.refetchQueries({ queryKey: ['quoteLadder', marketKey] });
    },
  });
};

/** Hard-reseed line + (x|p, P). Invalidates quote / OB / override (baseline lock). */
export const useReseedMarketPosterior = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ marketKey, line, P, x, p, lockBaseline, actedBy }) =>
      api.reseedMarketPosterior(marketKey, { line, P, x, p, lockBaseline, actedBy }),
    onSuccess: async (data) => {
      const marketKey = data.market_key;
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketOverride(marketKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.manualInterventions });
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteHistory', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteLadder', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quoteUpdates', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['quotePrices', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['diagnostics', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['entriesDiagnostics', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['competitorSignals', marketKey] });
      await prefetchFreshQuote(queryClient, marketKey);
      await queryClient.refetchQueries({ queryKey: ['quoteLadder', marketKey] });
    },
  });
};

export const useSetMuteBookCompetitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ marketKey, sportsbook, enabled, reason, muted_by: mutedBy }) =>
      api.setMuteBookCompetitor(marketKey, {
        sportsbook,
        enabled,
        reason,
        muted_by: mutedBy,
      }),
    onSuccess: async (data) => {
      const marketKey = data.market_key;
      queryClient.invalidateQueries({ queryKey: ['orderBook', marketKey] });
      queryClient.invalidateQueries({ queryKey: ['competitorSignals', marketKey] });
      queryClient.invalidateQueries({ queryKey: queryKeys.quote(marketKey) });
    },
  });
};

// =============================================================================
// Opta live hooks
// =============================================================================

export const useLiveKillSwitch = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.liveKillSwitch,
    queryFn: api.fetchLiveKillSwitch,
    staleTime: 5_000,
    refetchInterval: 15_000,
    ...options,
  });
};

export const useSetLiveKillSwitch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.setLiveKillSwitch(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liveKillSwitch });
      queryClient.invalidateQueries({ queryKey: ['optaLiveFixtures'] });
    },
  });
};

const invalidateFixtureLifecycleQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['optaLiveFixtures'] });
  queryClient.invalidateQueries({ queryKey: ['optaLiveDashboard'] });
};

export const useSetFixtureLifecycleStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId, ...body }) => api.setFixtureLifecycleStatus(gameId, body),
    onSuccess: () => invalidateFixtureLifecycleQueries(queryClient),
  });
};

export const useClearFixtureStatusOverride = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId, fanoutWriters = true }) =>
      api.clearFixtureStatusOverride(gameId, fanoutWriters),
    onSuccess: () => invalidateFixtureLifecycleQueries(queryClient),
  });
};

export const useOptaLiveFixtures = (
  retentionDays = 1,
  options = {},
  liveCoveredOnly = false,
  lookaheadDays = 2
) => {
  return useQuery({
    queryKey: queryKeys.optaLiveFixtures(
      retentionDays ?? 'config',
      liveCoveredOnly,
      lookaheadDays ?? 'config'
    ),
    queryFn: () =>
      api.fetchOptaLiveFixtures({
        retention_days: retentionDays ?? 1,
        lookahead_days: lookaheadDays ?? 2,
        live_covered_only: liveCoveredOnly,
      }),
    refetchInterval: 10_000,
    ...options,
  });
};

export const useOptaLiveDashboard = (fixtureId, optaId = null, options = {}) => {
  return useQuery({
    queryKey: queryKeys.optaLiveDashboard(fixtureId, optaId),
    queryFn: () => api.fetchOptaLiveDashboard(fixtureId, { opta_id: optaId }),
    enabled: Boolean(fixtureId),
    refetchInterval: 5_000,
    ...options,
  });
};

// =============================================================================
// Config Hooks
// =============================================================================

export const useConfig = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: api.fetchConfig,
    staleTime: 5 * 60_000, // 5 min
    ...options,
  });
};

export const useConfigOverrideTunables = (profile, options = {}) =>
  useQuery({
    queryKey: ['configOverrideTunables', profile],
    queryFn: () => api.fetchConfigOverrideTunables(profile),
    enabled: Boolean(profile),
    staleTime: 10 * 60_000,
    ...options,
  });

export const useConfigOverridesSummary = (options = {}) =>
  useQuery({
    queryKey: queryKeys.configOverridesSummary,
    queryFn: api.fetchConfigOverridesSummary,
    staleTime: 30_000,
    ...options,
  });

export const useConfigOverridesView = (profile, marketId, options = {}) =>
  useQuery({
    queryKey: queryKeys.configOverridesView(profile, marketId),
    queryFn: () => api.fetchConfigOverridesView(profile, marketId),
    enabled: Boolean(profile),
    staleTime: 15_000,
    ...options,
  });

export const useConfigOverridePreview = (
  { profile, path, newValue, oldValue, marketId },
  options = {}
) =>
  useQuery({
    queryKey: [
      'configOverridePreview',
      profile,
      path,
      newValue,
      oldValue,
      marketId || null,
    ],
    queryFn: () =>
      api.fetchConfigOverridePreview({
        profile,
        path,
        newValue,
        oldValue,
        marketId,
      }),
    enabled: Boolean(profile && path && String(newValue ?? '').trim() !== ''),
    staleTime: 20_000,
    ...options,
  });

export const useSetConfigOverridePath = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.setConfigOverridePath,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.configOverridesSummary });
      if (variables?.profile) {
        queryClient.invalidateQueries({
          queryKey: ['configOverridesView', variables.profile],
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.config });
    },
  });
};

export const useRevertConfigOverrides = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.revertConfigOverrides,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.configOverridesSummary });
      if (variables?.profile) {
        queryClient.invalidateQueries({
          queryKey: ['configOverridesView', variables.profile],
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.config });
    },
  });
};

// =============================================================================
// Limits: config_limits view + limit_modifiers overrides + enforcement sync
// =============================================================================

export const useConfigLimits = (params = {}, options = {}) =>
  useQuery({
    queryKey: queryKeys.configLimits(params),
    queryFn: () => api.fetchConfigLimits(params),
    staleTime: 10_000,
    ...options,
  });

export const useLimitFilterOptions = (options = {}) =>
  useQuery({
    queryKey: queryKeys.limitFilterOptions,
    queryFn: api.fetchLimitFilterOptions,
    staleTime: 300_000,
    ...options,
  });

export const useBaseLimits = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['baseLimits', JSON.stringify(params || {})],
    queryFn: () => api.fetchBaseLimits(params),
    staleTime: 10_000,
    ...options,
  });

export const useLimitModifiers = (options = {}) =>
  useQuery({
    queryKey: queryKeys.limitModifiers,
    queryFn: api.fetchLimitModifiers,
    staleTime: 10_000,
    ...options,
  });

const invalidateLimits = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.limitModifiers });
  queryClient.invalidateQueries({ queryKey: ['configLimits'] });
  queryClient.invalidateQueries({ queryKey: ['baseLimits'] });
};

export const useCreateLimitModifier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createLimitModifier(body),
    onSuccess: () => invalidateLimits(queryClient),
  });
};

export const useRemoveLimitModifier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.removeLimitModifier(id),
    onSuccess: () => invalidateLimits(queryClient),
  });
};

export const useSyncLimitsToBQ = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.syncLimitsToBQ(),
    onSuccess: () => {
      // Sync to BQ folds current limits into limits_changelog; config_limits is a view over the
      // same state, so refresh it. (Change history reads the modifier tables, which are
      // invalidated by their own create/remove mutations — not affected by Sync to BQ.)
      queryClient.invalidateQueries({ queryKey: ['configLimits'] });
    },
  });
};

export const useMultiplierModifiers = (options = {}) =>
  useQuery({
    queryKey: queryKeys.multiplierModifiers,
    queryFn: api.fetchMultiplierModifiers,
    staleTime: 10_000,
    ...options,
  });

export const useMultipliers = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['multipliers', JSON.stringify(params || {})],
    queryFn: () => api.fetchMultipliers(params),
    staleTime: 10_000,
    ...options,
  });

const invalidateMultipliers = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.multiplierModifiers });
  queryClient.invalidateQueries({ queryKey: ['multipliers'] });
  queryClient.invalidateQueries({ queryKey: ['configLimits'] });
};

export const useCreateMultiplierModifier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createMultiplierModifier(body),
    onSuccess: () => invalidateMultipliers(queryClient),
  });
};

export const useRemoveMultiplierModifier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.removeMultiplierModifier(id),
    onSuccess: () => invalidateMultipliers(queryClient),
  });
};

export const useModelMarketRules = (scope = {}, options = {}) => {
  const sport_bucket = scope.sport_bucket || scope.sportBucket || 'wnba';
  const sport = scope.sport || 'basketball';
  const league = scope.league || 'wnba';
  return useQuery({
    queryKey: queryKeys.modelMarketRules({ sport_bucket, sport, league }),
    queryFn: () => api.fetchModelMarketRules({ sport_bucket, sport, league }),
    staleTime: 30_000,
    ...options,
  });
};

export const useModelMarketRegistry = (scope = {}, options = {}) => {
  const sport_bucket = scope.sport_bucket || scope.sportBucket || 'wnba';
  const sport = scope.sport || 'basketball';
  const league = scope.league || 'wnba';
  return useQuery({
    queryKey: queryKeys.modelMarketRegistry({ sport_bucket, sport, league }),
    queryFn: () => api.fetchModelMarketRegistry({ sport_bucket, sport, league }),
    staleTime: 5_000,
    ...options,
  });
};

export const useModelMarketObservations = (
  modelName,
  fixtureId,
  marketId,
  scope = {},
  options = {}
) => {
  const sport_bucket = scope.sport_bucket || scope.sportBucket || 'wnba';
  const sport = scope.sport || 'basketball';
  const league = scope.league || 'wnba';
  return useQuery({
    queryKey: queryKeys.modelMarketObservations(
      modelName,
      fixtureId,
      marketId,
      { sport_bucket }
    ),
    queryFn: () =>
      api.fetchModelMarketObservations({
        model_name: modelName,
        fixture_id: fixtureId,
        market_id: marketId,
        sport_bucket,
        sport,
        league,
      }),
    enabled: Boolean(modelName && fixtureId),
    staleTime: 5_000,
    ...options,
  });
};

export const useEnsureModelMarketFixture = () => {
  return useMutation({
    mutationFn: (body) => api.ensureModelMarketFixture(body),
  });
};

export const useEnsureModelMarketPlayers = () => {
  return useMutation({
    mutationFn: (body) => api.ensureModelMarketPlayers(body),
  });
};

export const usePutModelMarketFixtureSleeperLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.putModelMarketFixtureSleeperLink(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activeFixturesSummary });
    },
  });
};

export const useDeleteModelMarketFixtureSleeperLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => api.deleteModelMarketFixtureSleeperLink(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activeFixturesSummary });
    },
  });
};

export const useSearchModelMarketPlayers = () => {
  return useMutation({
    mutationFn: (params) => api.searchModelMarketPlayers(params),
  });
};

export const usePreviewModelMarketPlayers = () => {
  return useMutation({
    mutationFn: (body) => api.previewModelMarketPlayers(body),
  });
};

export const useApplyModelMarketPlayers = () => {
  return useMutation({
    mutationFn: (body) => api.applyModelMarketPlayers(body),
  });
};

export const useEnsureModelMarkets = () => {
  return useMutation({
    mutationFn: (body) => api.ensureModelMarkets(body),
  });
};

/** Operations -> Derived manual inputs. Polls: legs go stale on their own as books move. */
export const useDerivedManualInputs = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['derivedManualInputs', params],
    queryFn: () => api.fetchDerivedManualInputs(params),
    refetchInterval: 20000,
    ...options,
  });

export const useIngestModelMarketObservations = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.ingestModelMarketObservations(body),
    onSuccess: (_data, variables) => {
      const scope = {
        sport_bucket: variables?.sport_bucket,
        sport: variables?.sport,
        league: variables?.league,
      };
      queryClient.invalidateQueries({
        queryKey: queryKeys.modelMarketRegistry(scope),
      });
      queryClient.invalidateQueries({ queryKey: ['modelMarketObservations'] });
      queryClient.invalidateQueries({ queryKey: ['derivedManualInputs'] });
    },
  });
};

export const useIngestModelMarketObservationsBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.ingestModelMarketObservationsBatch(body),
    onSuccess: (_data, variables) => {
      const scope = {
        sport_bucket: variables?.sport_bucket,
        sport: variables?.sport,
        league: variables?.league,
      };
      queryClient.invalidateQueries({
        queryKey: queryKeys.modelMarketRegistry(scope),
      });
      queryClient.invalidateQueries({ queryKey: ['modelMarketObservations'] });
    },
  });
};

// Risk Rank -------------------------------------------------------------------

export const useRiskRankConfig = () =>
  useQuery({ queryKey: ['riskRank', 'config'], queryFn: api.fetchRiskRankConfig, staleTime: 300_000 });

export const useRiskRankBoard = (league) =>
  useQuery({
    queryKey: ['riskRank', 'board', league],
    queryFn: () => api.fetchRiskRankBoard(league),
    enabled: !!league,
    refetchInterval: 30_000,
  });

export const useRiskRankCandidates = (league, scopeType, scopeId) =>
  useQuery({
    queryKey: ['riskRank', 'candidates', league, scopeType, scopeId],
    queryFn: () => api.fetchRiskRankCandidates(league, scopeType, scopeId),
    enabled: !!league && !!scopeType && !!scopeId,
    staleTime: 15_000,
  });

const useRiskRankMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['riskRank'] }),
  });
};

export const useSetRiskRankOverride = () =>
  useRiskRankMutation(({ league, body }) => api.setRiskRankOverride(league, body));

export const useClearRiskRankOverride = () =>
  useRiskRankMutation(({ league, id }) => api.clearRiskRankOverride(league, id));

export const useRecomputeRiskRank = () =>
  useRiskRankMutation((league) => api.recomputeRiskRank(league));
